import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, Plus, Stethoscope } from 'lucide-react';
import { appointmentService } from '../../../lib/services';
import { encounterService } from '../api/encounters';
import { useStore } from '../../../lib/store';
import { getDoctorIdFromUser, isDoctor } from '../../../lib/rbac';
import type { Appointment } from '../../../lib/supabase';
import type { Encounter } from '../types';

export default function ConsultationWorklist() {
  const { user } = useStore();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [loading, setLoading] = useState(true);

  const doctorId = getDoctorIdFromUser(user);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      let appointmentsData: Appointment[];
      let encountersData: Encounter[];

      if (isDoctor(user) && doctorId) {
        [appointmentsData, encountersData] = await Promise.all([
          appointmentService.getByDoctor(doctorId),
          encounterService.getTodaysEncountersByDoctor(doctorId)
        ]);
      } else {
        [appointmentsData, encountersData] = await Promise.all([
          appointmentService.getAll(),
          encounterService.getTodaysEncounters()
        ]);
      }
      
      const today = new Date().toISOString().split('T')[0];
      const todaysAppointments = appointmentsData.filter(apt => 
        apt.appointment_date.startsWith(today)
      );
      
      setAppointments(todaysAppointments);
      setEncounters(encountersData);
    } catch (error) {
      console.error('Failed to load consultation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasEncounter = (appointmentId: string) => {
    return encounters.some(enc => enc.appointmentId === appointmentId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Consultation Worklist</h1>
          <p className="text-sm text-neutral-600">Today's appointments and consultations</p>
        </div>
        {!isDoctor(user) && (
          <Link
            to="/consultations/new"
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <Plus className="w-4 h-4" />
            <span>New Consultation</span>
          </Link>
        )}
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Today's Appointments</p>
              <p className="text-2xl font-bold text-neutral-900">{appointments.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Stethoscope className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Completed Consultations</p>
              <p className="text-2xl font-bold text-neutral-900">{encounters.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-amber-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Pending Consultations</p>
              <p className="text-2xl font-bold text-neutral-900">
                {appointments.length - encounters.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Appointments List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Today's Appointments</h3>
        </div>
        
        {appointments.length === 0 ? (
          <div className="p-6 text-center text-neutral-500">
            No appointments scheduled for today
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Doctor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {appointments.map((appointment) => (
                  <tr key={appointment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {appointment.start_time} - {appointment.end_time}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {appointment.patient?.name || 'Unknown Patient'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {appointment.doctor?.name || 'Unknown Doctor'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {hasEncounter(appointment.id) ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Completed
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {hasEncounter(appointment.id) ? (
                          <Link
                            to={`/consultations/${encounters.find(e => e.appointmentId === appointment.id)?.id}`}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Consultation"
                          >
                            <Stethoscope className="w-4 h-4" />
                          </Link>
                        ) : (
                          <Link
                            to={`/consultations/new?patientId=${appointment.patient_id}&appointmentId=${appointment.id}&doctorId=${appointment.doctor_id}`}
                            className="text-green-600 hover:text-green-900"
                            title="Start Consultation"
                          >
                            <Plus className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
